// src/pages/chat.js

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../contexts/PageContext";
import { useChatRooms } from "../hooks/useChat";
import { useQueryClient } from '@tanstack/react-query';
import { chatKeys } from "../hooks/useChat";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import ChatListItem from "../components/chat/ChatListItem";

const Chat = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const queryClient = useQueryClient();
  
  const {
    data: rooms = [],
    isLoading,
    error,
    refetch 
  } = useChatRooms();

  useEffect(() => {
    updatePageTitle(t("chat.messages"), t("chat.startConversation"));
  }, [updatePageTitle, t]);

  // Real-time message handling for conversation list updates
  useEffect(() => {
    const handleNewMessage = (event) => {
      // eslint-disable-next-line no-unused-vars
      const { message, roomId, senderId } = event.detail;
      
      console.log("Real-time message received, updating conversation list:", message);
      
      // Invalidate and refetch rooms to update last message and unread counts
      queryClient.invalidateQueries({ 
        queryKey: chatKeys.rooms() 
      });
    };

    // Listen for real-time message events
    window.addEventListener('newMessage', handleNewMessage);
    
    return () => {
      window.removeEventListener('newMessage', handleNewMessage);
    };
  }, [queryClient]);

  // Handle chat selection
  const handleChatSelect = (chat) => {
    navigate(`/chat/${chat.roomId || chat._id}`);
  };

  // Calculate total unread messages
  const totalUnreadMessages = rooms.reduce((total, chat) => total + (chat.unreadCount || chat.unreadMessages || 0), 0);

  if (isLoading) {
    return <LoadingSpinner message={t("pages.loading.pleaseWait")} />;
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error.message || t("errors.failedToLoad")} 
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-[#F15213] to-[#F15213]">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-white">{t("chat.messages")}</h1>
                <p className="text-orange-100 mt-1">
                  {rooms.length} {t("chat.groupChat")}
                  {totalUnreadMessages > 0 && ` • ${totalUnreadMessages} ${t("notifications.unreadCount", { count: totalUnreadMessages })}`}
                </p>
              </div>
              
              {/* Unread Count Badge */}
              {totalUnreadMessages > 0 && (
                <div className="flex items-center space-x-2 bg-red-500 px-3 py-2 rounded-full">
                  <span className="text-white font-bold">{totalUnreadMessages}</span>
                  <span className="text-white text-sm">{t("notifications.unreadCount", { count: totalUnreadMessages })}</span>
                </div>
              )}
            </div>
          </div>

          {/* Chat List */}
          <div className="divide-y divide-gray-100">
            {rooms.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-2.697-.413l-3.178 1.589a1 1 0 01-1.414-1.414l1.589-3.178A8.955 8.955 0 713 12a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("chat.noMessages")}
                </h3>
                <p className="text-gray-500">
                  {t("chat.startConversation")}
                </p>
              </div>
            ) : (
              rooms.map((chat) => (
                <ChatListItem 
                  key={chat.roomId || chat._id} 
                  chat={chat} 
                  onClick={() => handleChatSelect(chat)}
                  type="private"
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{t("chat.messages")}</span>
              <span>
                {rooms.length} {rooms.length === 1 ? t("chat.privateChat") : t("chat.groupChat")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
